// One share as a row of /shares: its state as a dot, its kind as an icon, its
// title the link to it — under it, a terminal's tmux session and what runs in
// it, an agent's CLI or A2A card and its session — then what it is doing
// while it is live (its state, the permissions it waits on), the turns and
// cost an ended one came to, or that one neither live nor ended has lost its
// sidecar, which its owner may reconnect (byj-fym); then who is in it and
// how long it has run while it is live, or the day it ended, or started for
// one not ended. Its actions (Edit, Delete or Remove) are behind its ⋯ menu;
// an ended share of mine has Resume where who was in it stands, which opens
// the command that resumes it with its Copy (byj-1wv.12). A group's page
// lists its shares as these rows too, without the link to the group they are
// in (byj-1wv.51.5).
import { useNow } from "../now";
import type { ShareSummary } from "../api";
import { kindIcon, kindOf } from "../kinds";
import { t, money, when, whenShort, word, phrases } from "../i18n";
import { Link } from "./Nav";
import { canResume, ResumeButton } from "./ResumeHint";
import { RowMenu, type MenuItem } from "./RowMenu";
import { SelectBox } from "./Selection";


export function ShareRow({ share: s, menu, select, groupLink = true }: {
  share: ShareSummary;
  menu: MenuItem[];
  /** While the list is being selected from, and this row may be picked. */
  select?: { picked: Set<string>; toggle: (id: string) => void };
  /** Whether a share in a group links to it: not on that group's own page. */
  groupLink?: boolean;
}) {
  const k = kindOf(s.source);
  const title = s.title || s.id;
  const now = useNow(s.live);
  const resume = canResume(s);
  const sess = s.session;
  const agent = k === "agent" ? s.agent : undefined;
  // live, its sidecar connected; ended; or neither, its sidecar gone
  const ended = !s.live && !!s.ended_at;
  const gone = !s.live && !s.ended_at;
  const at = ended ? s.ended_at! : s.started_at;
  const sub = [
    ...(k === "term"
      ? // a terminal's kind is its tab's: its tmux session, where it differs
        // from the title, and what runs in it
        [sess?.id && sess.id !== title ? t("shares:tmuxSession", { name: sess.id }) : null, sess?.label || null]
      : // an agent's says which CLI it is, or which A2A agent, then its session
        [agent ? t("shares:agentSkills", { name: agent.name, count: agent.skills.length }) : word("shares:kindName", k) ?? k, !agent && sess?.label ? t("shares:sessionLabel", { label: sess.label }) : null]),
    // a live terminal nobody may type into from the page
    k === "term" && s.live && s.caps?.terminal && !s.caps.input ? t("shares:readOnly") : null,
    s.role !== "owner" ? t("shares:sharedBy", { name: s.owner.name, role: word("common:role", s.role) ?? s.role }) : null,
    s.assignment ? s.assignment.title : s.class_id ? t("shares:classShare") : null,
  ].filter((x): x is string => !!x);
  const subTitle = [sub.join(" · "), agent?.skills.map((x) => x.name || x.id).join(", ")].filter(Boolean).join("\n");
  const group = groupLink ? s.group : undefined;
  return (
    <li className={`share-row ${s.live ? "is-live" : ended ? "is-ended" : "is-disconnected"}`}>
      <span className="row-lead">
        {select && <SelectBox id={s.id} title={title} picked={select.picked} toggle={select.toggle} />}
        <span className="state-dot" aria-hidden="true" />
        {/* a share whose sidecar went says so in its meta */}
        {!gone && <span className="sr-only">{s.live ? t("shares:live") : t("shares:ended")}</span>}
      </span>
      <span className={`kind-box kind-${k}`} aria-hidden="true" title={word("shares:kindName", k) ?? k}>
        {kindIcon[k]}
      </span>
      <div className="row-main">
        <Link to={`/s/${encodeURIComponent(s.id)}`} className="row-title">
          {title}
        </Link>
        {(sub.length > 0 || group) && (
          <div className="row-sub" title={subTitle}>
            {sub.join(" · ")}
            {group && (
              <>
                {sub.length > 0 && " · "}
                <Link to={`/g/${encodeURIComponent(group)}`}>{t("shares:inGroup")}</Link>
              </>
            )}
          </div>
        )}
      </div>
      <div className="row-meta">
        {/* what a live one is doing, what an ended one came to (a terminal
            reports no turns), or that its sidecar is gone; empty for a row
            with none of these, its column kept so the rows line up (narrow,
            an empty cell takes no room) */}
        <span className="row-stats">
          {s.live && s.state && <span className={`chip state-chip state-chip-${s.state}`}>{word("classes:stateBadge", s.state) ?? s.state}</span>}
          {s.live && !!s.pending_permissions && <span className="warn">{t("classes:pendingN", { count: s.pending_permissions })}</span>}
          {ended && (s.turns || s.cost_usd) ? t("session:statusBrief", { count: s.turns ?? 0, cost: money(s.cost_usd ?? 0) }) : null}
          {gone && <span className="chip chip-warn">{t("shares:notConnected")}</span>}
        </span>
        {/* who is in it, while it is live (the relay counts nobody in an ended
            one); an ended share of mine has Resume there instead */}
        <span className="row-people">
          {resume ? <ResumeButton share={s.id} title={title} /> : s.live ? t("shares:participants", { count: s.participants }) : null}
        </span>
        {/* a live one, how long it has run, kept current; an ended one, the
            day it ended; one whose sidecar went, the day it started */}
        <time className="row-time" dateTime={at} title={ended ? t("shares:endedAt", { when: when(at) }) : t("shares:startedAt", { when: when(at) })}>
          {s.live ? phrases.elapsed(now - Date.parse(s.started_at)) : whenShort(at)}
        </time>
      </div>
      <RowMenu label={t("shares:moreFor", { title })} items={menu} />
    </li>
  );
}
